import { useEffect, useRef, useState } from "react";
import { copy } from "../copy";
import type { DraftImage } from "../domain/draft";
import { imageTarget } from "../domain/revisions";

export function DraftGallery({
  images,
  onMark,
  onIndex,
}: {
  images: readonly DraftImage[];
  onMark: (index: number) => void;
  onIndex?: (index: number) => void;
}) {
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<Record<number, boolean>>({});
  const [zoom, setZoom] = useState(false);
  const drag = useRef<number | null>(null);
  const moved = useRef(false);
  const key = images.map((image) => image.url).join("|");
  const current = Math.min(index, Math.max(images.length - 1, 0));
  const slide = images[current];

  useEffect(() => {
    setIndex(0);
    setFailed({});
    setZoom(false);
  }, [key]);

  useEffect(() => {
    onIndex?.(current + 1);
  }, [current, onIndex]);

  useEffect(() => {
    if (!zoom) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setZoom(false);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [zoom]);

  function go(step: number) {
    if (images.length < 2) return;
    setIndex((value) => (value + step + images.length) % images.length);
  }

  if (!slide) {
    return (
      <div className="phone-cover">
        <span>{copy.flow.coverMissing}</span>
      </div>
    );
  }

  const show = showable(slide.url);

  return (
    <div className="phone-gallery">
      <div
        className="phone-cover"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") go(-1);
          if (event.key === "ArrowRight") go(1);
        }}
        onPointerDown={(event) => {
          drag.current = event.clientX;
          moved.current = false;
        }}
        onPointerMove={(event) => {
          if (drag.current != null && Math.abs(event.clientX - drag.current) > 8) moved.current = true;
        }}
        onPointerUp={(event) => {
          if (drag.current == null) return;
          const delta = event.clientX - drag.current;
          drag.current = null;
          if (delta > 40) go(-1);
          else if (delta < -40) go(1);
        }}
      >
        {show && !failed[current] ? (
          <button
            type="button"
            className="phone-zoom"
            onClick={() => {
              if (moved.current) {
                moved.current = false;
                return;
              }
              onMark(current + 1);
              setZoom(true);
            }}
          >
            <img src={show} alt={slide.caption || imageTarget(current + 1)} onError={() => setFailed((value) => ({ ...value, [current]: true }))} />
          </button>
        ) : (
          <span>{copy.review.imageFailed}</span>
        )}
        <p className="phone-count">
          {images.length > 1 ? (
            <button type="button" className="phone-step" onClick={() => go(-1)}>
              上一张
            </button>
          ) : null}
          <span>
            {current + 1}/{images.length}
          </span>
          {images.length > 1 ? (
            <button type="button" className="phone-step" onClick={() => go(1)}>
              下一张
            </button>
          ) : null}
        </p>
      </div>
      {images.length > 0 ? (
        <div className="phone-dots">
          {images.map((image, dot) => (
            <button
              key={`${image.url}-${dot}`}
              type="button"
              className="phone-dot"
              aria-label={imageTarget(dot + 1)}
              aria-current={dot === current}
              onClick={() => setIndex(dot)}
            />
          ))}
        </div>
      ) : null}
      <div className="phone-thumbs">
        {images.map((image, thumb) => {
          const src = showable(image.url);
          return (
            <button
              key={`${image.url}-thumb-${thumb}`}
              type="button"
              className="phone-thumb"
              aria-label={imageTarget(thumb + 1)}
              aria-current={thumb === current}
              onClick={() => {
                setIndex(thumb);
                onMark(thumb + 1);
              }}
            >
              {src ? <img src={src} alt="" /> : <span>{thumb + 1}</span>}
            </button>
          );
        })}
      </div>
      {zoom && show ? (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label={imageTarget(current + 1)} onMouseDown={() => setZoom(false)}>
          <img src={show} alt={slide.caption || imageTarget(current + 1)} onMouseDown={(event) => event.stopPropagation()} />
        </div>
      ) : null}
    </div>
  );
}

function showable(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
    return url;
  } catch {
    return null;
  }
}
